import {ChangeDetectorRef, Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {PageHeaderComponent} from "../../../libs/header/page-header/page-header.component";
import {TranslatePipe} from "@ngx-translate/core";
import {StateService} from "../../../services/state/state.service";
import {OrgDto} from "../../../repositories/dto/org.dto";
import {BehaviorSubject, EMPTY, Subscription, tap} from "rxjs";
import {
  arraysEqualUnordered,
  arraysNonEqualUnordered,
  deserializeDto,
  isDefined,
  isFalse,
  isNumeric,
  isTrue,
  nonBlank,
  safeSplit,
  serializeDto,
  unsubscribe
} from "../../../libs/utils/epic.utils";
import {NgTemplateOutlet} from "@angular/common";
import {AccStateEnum} from "../../../services/state/acc-state.enum";
import {UserDto} from "../../../repositories/dto/user.dto";
import {CardComponent} from "../../../libs/card/card.component";
import {CardHeaderComponent} from "../../../libs/card/card-header/card-header.component";
import {CardBodyComponent} from "../../../libs/card/card-body/card-body.component";
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray} from "@angular/cdk/drag-drop";
import {SettingsService} from "../../../services/settings.service";
import {SettingKey} from "../../../base/setting-key.enum";
import {IconComponent} from "../../../libs/icon/icon.component";
import {PermissionService} from "../../../services/auth/permission.service";
import {OverviewService} from "../../../services/overview.service";
import {OverviewDto} from "../../../repositories/dto/overview-dto";
import {Paths} from "../../../paths";
import {Router} from "@angular/router";
import {ButtonComponent} from "../../../libs/button/button.component";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {FileSizePipe} from "../../../libs/pipes/file-size.pipe";
import {NumberPipe} from "../../../libs/pipes/number.pipe";
import {Permissions} from "../../../services/auth/permissions.item";
import {SettingDto} from "../../../repositories/dto/setting.dto";
import {LoggingService} from "../../../services/logger/logging.service";
import {AmountPipe} from "../../../libs/pipes/amount.pipe";
import {Feature} from "../../../libs/enum/feature.enum";
import {UrlService} from "../../../services/url.service";
import {FirstStepsComponent} from "../first-steps/first-steps.component";
import {environment} from "../../../../environments/environment";
import {filter, switchMap, take} from "rxjs/operators";

@Component({
  selector: 'acc-overview',
  imports: [
    PageHeaderComponent,
    TranslatePipe,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    CdkDropList,
    CdkDrag,
    NgTemplateOutlet,
    CdkDragHandle,
    IconComponent,
    ButtonComponent,
    NgbTooltip,
    FileSizePipe,
    NumberPipe,
    AmountPipe,
    FirstStepsComponent
  ],
  templateUrl: './overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './overview.component.css'
})
export class OverviewComponent implements OnInit, OnDestroy {

  @ViewChild('contactTpl')
  public contactTpl!: TemplateRef<any>;

  @ViewChild('articleTpl')
  public articleTpl!: TemplateRef<any>;

  @ViewChild('vehicleTpl')
  public vehicleTpl!: TemplateRef<any>;

  @ViewChild('invoiceTpl')
  public invoiceTpl!: TemplateRef<any>;

  @ViewChild('receiptTpl')
  public receiptTpl!: TemplateRef<any>;

  @ViewChild('mediaTpl')
  public mediaTpl!: TemplateRef<any>;

  @ViewChild('assignedOrgTpl')
  public assignedOrgTpl!: TemplateRef<any>;

  @ViewChild('deathTpl')
  public deathTpl!: TemplateRef<any>;

  protected readonly AccStateEnum: typeof AccStateEnum = AccStateEnum;
  protected readonly isIronBackupViewer: boolean = environment.ironBackupViewer;
  protected state: AccStateEnum | undefined;
  protected org: OrgDto | undefined;
  protected user: UserDto | undefined;
  protected templates: TemplateRef<any>[] | undefined;
  protected overview: OverviewDto | undefined;
  protected perms: Permissions;

  private readonly stateService: StateService = inject(StateService);
  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly router: Router = inject(Router);
  private readonly overviewService: OverviewService = inject(OverviewService);
  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly urlService: UrlService = inject(UrlService);

  private readonly delimiter: string = ';';
  private readonly subs: Subscription[] = [];
  private readonly userOrgSubs: Subscription[] = [];
  //private readonly timeoutOrgSubs: any[] = [];
  private readonly settingsSorting: BehaviorSubject<SettingDto | undefined> = new BehaviorSubject<SettingDto | undefined>(undefined);
  private allowedContainers: Map<string, TemplateRef<any>> = new Map<string, TemplateRef<any>>();
  private containers: Map<string, TemplateRef<any>> = new Map<string, TemplateRef<any>>();
  private sorting: string[] = [];
  private setting: SettingDto | undefined;

  constructor() {
    this.perms = this.permissionService.getPerms();
    this.settingsService.getSettingOnChange(SettingKey.USER_OVERVIEW_ORDER).subscribe((s) => this.settingsSorting.next(s));
    this.settingsSorting.pipe(
      filter((s) => isDefined(s)),
      filter((s) => s?.value !== this.setting?.value)
    ).subscribe((s) => this.initSorting(s));
    this.stateService.getUser().subscribe((u) => this.user = u);
    this.stateService.getOrg().subscribe((o) => this.org = o);
  }

  ngOnInit(): void {
    this.subs.push(
      this.permissionService.accChanged().subscribe((p) => this.perms = p),
      // “getStateDistinct” must not be used, as it is possible to switch from ORG to ORG.
      // A delay of 100 is not necessary, but it is included anyway for safety reasons.
      /*this.stateService.getState().pipe(delay(150)).subscribe((s) => {
        this.state = s;
        this.log.debug('Overview state changed to: {}', [s]);
        unsubscribe(this.userOrgSubs);
        unsubscribe(this.timeoutOrgSubs);
        if (this.state === AccStateEnum.ORG && isDefined(this.org)) {
          this.loadOrgOverview();
          this.timeoutOrgSubs.push(setInterval(() => this.loadOrgOverview(), OverviewService.REFRESH_DELAY_SEC * 1000));
        } else if (this.state === AccStateEnum.USER && isDefined(this.user)) {
          this.loadUserOverview();
          this.timeoutOrgSubs.push(setInterval(() => this.loadUserOverview(), OverviewService.REFRESH_DELAY_SEC * 1000));
        } else {
          this.log.error('No Org or User found for Overview with state "{}"', [this.state]);
        }
      }),*/
      this.stateService.getState().pipe(switchMap((s) => {
        unsubscribe(this.userOrgSubs);
        this.state = s;
        switch (s) {
          case AccStateEnum.ORG:
            return this.stateService.getOrg().pipe(
              filter((o) => isDefined(o)),
              take(1),
              tap((o) => {
                this.org = o;
                this.loadOrgOverview();
              })
            );
          case AccStateEnum.USER:
            return this.stateService.getUser().pipe(
              filter((u) => isDefined(u)),
              take(1),
              tap((u) => {
                this.user = u;
                this.loadUserOverview();
              })
            );
          default:
            this.log.error("Overview unhandled AccState: {}", [s]);
            return EMPTY;
        }
      })).subscribe()
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
    unsubscribe(this.userOrgSubs);
    //unsubscribe(this.timeoutOrgSubs);
  }

  protected getSubTitle(): string {
    if (isDefined(this.org)) {
      return ' - ' + this.org.name;
    }
    return '';
  }

  private initOverview(): void {
    if (isDefined(this.settingsSorting.value)) {
      this.initSorting(this.settingsSorting.value);
      this.cdr.detectChanges();
    }
  }

  private initSorting(setting: SettingDto): void {
    this.getCurrentSorting(setting).then((s) => {
      this.sorting = [];
      this.initContainers();
      this.reorderMap(s, true);
    }).catch(() => void 0);
  }

  private initContainers(): void {
    this.allowedContainers.clear();
    if (this.state === AccStateEnum.ORG) {
      if (this.perms.contact.read) {
        this.allowedContainers.set('contact', this.contactTpl);
      }
      if (this.perms.article.read) {
        this.allowedContainers.set('article', this.articleTpl);
      }
      if (this.perms.contactVehicle.read && this.isFeatureEnabled(Feature.CONTACT_VEHICLE)) {
        this.allowedContainers.set('vehicle', this.vehicleTpl);
      }
      if (this.perms.invoice.read) {
        this.allowedContainers.set('invoice', this.invoiceTpl);
      }
      if (this.perms.invoice.read) {
        this.allowedContainers.set('receipt', this.receiptTpl);
      }
      if (this.perms.billing.read) {
        this.allowedContainers.set('media', this.mediaTpl);
      }
      if (this.isFeatureEnabled(Feature.FUNERAL)) {
        this.allowedContainers.set('death', this.deathTpl);
      }
    } else if (this.state === AccStateEnum.USER) {
      this.allowedContainers.set('assignedOrg', this.assignedOrgTpl);
    }
  }

  private reorderMap(sorting: string[], init: boolean = false): void {
    const containerKeys: string[] = Array.from(this.allowedContainers.keys());
    this.sorting = isDefined(sorting) && arraysEqualUnordered(sorting, containerKeys) ? sorting : containerKeys;
    this.containers = new Map<string, TemplateRef<any>>(
      this.sorting.filter((key) => this.allowedContainers.has(key))
        .map((key) => [key, this.allowedContainers.get(key)!])
    );
    this.templates = Array.from(this.containers.values());
    if (isFalse(init) || arraysNonEqualUnordered(sorting || [], this.sorting)) {
      this.log.debug("Overview sorting updating");
      this.settingsService.updateUserSetting(SettingKey.USER_OVERVIEW_ORDER, this.buildSetting(this.sorting)).then();
    }
  }

  private getCurrentSorting(setting: SettingDto): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      this.setting = setting;
      const data: Map<string, string> = this.parseSetting(setting);
      if (isDefined(this.state) && (isDefined(this.org) || isDefined(this.user))) {
        if (this.state === AccStateEnum.ORG && isDefined(this.org) && data.has(this.org.id)) {
          resolve(safeSplit(data.get(this.org.id), this.delimiter));
        } else if (this.state === AccStateEnum.USER && isDefined(this.user) && data.has(this.user.id)) {
          resolve(safeSplit(data.get(this.user.id), this.delimiter));
        } else {
          this.log.info('Overview sorting not found, fallback to default');
          resolve([]);
        }
      } else {
        reject();
      }
    });
  }

  private parseSetting(setting: SettingDto): Map<string, string> {
    try {
      return deserializeDto(setting.value || '{}');
    } // eslint-disable-next-line @typescript-eslint/no-unused-vars
    catch (err) {
      return new Map<string, string>();
    }
  }

  protected drop(event: CdkDragDrop<TemplateRef<any>, TemplateRef<any>>): void {
    if (isDefined(this.sorting) && this.sorting.length > 0) {
      moveItemInArray(this.sorting, event.previousIndex, event.currentIndex);
      this.reorderMap(this.sorting);
    }
  }

  private buildSetting(sorting: string[]): string {
    const setting: SettingDto = this.setting || new SettingDto();
    const data: Map<string, string> = this.parseSetting(setting);
    const sort: string = isDefined(sorting) ? sorting.join(this.delimiter) : '';
    if (this.state === AccStateEnum.ORG && isDefined(this.org)) {
      data.set(this.org.id, sort);
    } else if (this.state === AccStateEnum.USER && isDefined(this.user)) {
      data.set(this.user.id, sort);
    } else {
      this.log.warn('Overview sorting not set');
    }
    return serializeDto(data);
  }

  private loadOrgOverview(): void {
    this.userOrgSubs.push(this.overviewService.getForOrg().subscribe((o) => {
      this.overview = o;
      this.log.debug('Set Overview with state "{}" for org "{}": {}', [this.state, this.org?.id, o]);
      this.initOverview();
    }));
  }

  private loadUserOverview(): void {
    this.userOrgSubs.push(this.overviewService.getForUser().subscribe((o) => {
      this.overview = o;
      this.log.debug('Set Overview with state "{}" for user "{}": {}', [this.state, this.user?.id, o]);
      this.initOverview();
    }));
  }

  // ##########################################################################
  // #### GoTo and Click actions
  // ##########################################################################

  protected gotoContactList(filter?: string): void {
    if (nonBlank(filter)) {
      this.router.navigate([Paths.CONTACT_LIST, {filterCategory: filter}]).then();
    } else {
      this.router.navigate([Paths.CONTACT_LIST]).then();
    }
  }

  protected addNewContact(): void {
    this.router.navigate([Paths.CONTACT_EDIT, {id: 'new'}]).then();
  }

  protected gotoArticleList(): void {
    this.router.navigate([Paths.ARTICLE_LIST]).then();
  }

  protected addNewArticle(): void {
    this.router.navigate([Paths.ARTICLE_EDIT, {id: 'new'}]).then();
  }

  protected gotoContactVehicleList(): void {
    this.router.navigate([Paths.CONTACT_VEHICLE_LIST]).then();
  }

  protected addNewContactVehicle(): void {
    this.router.navigate([Paths.CONTACT_VEHICLE_EDIT, {id: 'new'}]).then();
  }

  protected createNewInvoice(): void {
    this.router.navigate([Paths.INVOICE_EDIT, {id: 'new'}]).then();
  }

  protected gotoInvoiceList(filterState?: string, filterPayment?: boolean, filterYear?: string | number): void {
    const params: any = {};
    if (isDefined(filterState)) {
      params['f-state'] = filterState;
    }
    if (isDefined(filterPayment)) {
      params['filterPayment'] = filterPayment;
    }
    if (isDefined(filterYear) && isNumeric(filterYear)) {
      params['f-invoice-year'] = filterYear;
    }
    this.router.navigate([Paths.INVOICE_LIST, params]).then();
  }

  protected createNewReceipt(): void {
    this.router.navigate([Paths.RECEIPT_EDIT, {id: 'new'}]).then();
  }

  protected gotoReceiptList(filter?: string, filterValue?: string): void {
    if (isDefined(filter) && isDefined(filterValue)) {
      this.router.navigate([Paths.RECEIPT_LIST, {[filter]: filterValue}]).then();
    } else {
      this.router.navigate([Paths.RECEIPT_LIST]).then();
    }
  }

  protected gotoMediaList(): void {
    this.router.navigate([Paths.MEDIA_LIST]).then();
  }

  protected gotoSheetList(): void {
    this.router.navigate([Paths.SHEET_LIST]).then();
  }

  protected gotoAssignedOrgList(): void {
    this.router.navigate([Paths.ORG_LIST]).then();
  }

  protected gotoContactVehicleInspectionList(): void {
    this.urlService.navigate([Paths.CONTACT_VEHICLE_INSPECTION, {inspectionFilter: 'true'}]).then();
  }

  protected gotoContactVehicleTireChangeList(): void {
    this.urlService.navigate([Paths.CONTACT_VEHICLE_TIRES, {tireChangeFilter: 'true'}]).then();
  }

  protected gotoOrgAdd(): void {
    this.router.navigate([Paths.ORG_ADD]).then();
  }

  protected gotoDeathList(tdFilterValue?: number): void {
    const params: any = {};
    if (isDefined(tdFilterValue)) {
      params['f-time-of-death'] = tdFilterValue;
    }
    this.router.navigate([Paths.FUNERAL_LIST, params]).then();
  }

  private isFeatureEnabled(featureName: Feature | string): boolean {
    const feature: Feature = typeof featureName === 'string' ? Feature[featureName as keyof typeof Feature] : featureName;
    return isTrue(this.org?.isFeatureEnabled(feature));
  }
}
